/**
 * Rosabella clone — behaviour for the cloned tryrosabella.com sections.
 *
 * Everything here is namespaced (`rosa-*` custom elements, `.rbx` scope) so it
 * cannot collide with Horizon's own components — Horizon already defines
 * `header-drawer`, `predictive-search-component`, etc.
 */
(function () {
  'use strict';

  // Every cloned section links this file, so several <script> tags can point
  // here on one page. The browser fetches once but executes per tag — bail out
  // after the first run so listeners are not bound twice.
  if (window.__rosabellaLoaded) return;
  window.__rosabellaLoaded = true;

  var root = document.documentElement;

  /* ------------------------------------------------------------------ *
   * Viewport / header measurements consumed by the menu-drawer CSS.
   * ------------------------------------------------------------------ */
  function setViewportVars() {
    root.style.setProperty('--viewport-height', window.innerHeight + 'px');

    var wrapper = document.querySelector('.rbx .header-wrapper');
    if (!wrapper) return;
    root.style.setProperty('--header-height', Math.round(wrapper.offsetHeight) + 'px');
    root.style.setProperty(
      '--header-bottom-position',
      Math.round(wrapper.getBoundingClientRect().bottom) + 'px'
    );
  }

  setViewportVars();
  window.addEventListener('resize', setViewportVars);
  window.addEventListener('scroll', setViewportVars, { passive: true });
  document.addEventListener('DOMContentLoaded', setViewportVars);

  /* ------------------------------------------------------------------ *
   * Announcement bar carousel (Dawn's slideshow-component).
   * ------------------------------------------------------------------ */
  var RosaSlideshow = class extends HTMLElement {
    connectedCallback() {
      this.slider = this.querySelector('.slider');
      this.slides = Array.prototype.slice.call(this.querySelectorAll('.slider__slide'));
      if (!this.slider || this.slides.length < 2) return;

      this.index = 0;
      this.speed = parseInt(this.slider.dataset.speed || '5', 10) * 1000;
      this.autoplay = this.slider.dataset.autoplay === 'true';

      var prev = this.querySelector('.slider-button--prev');
      var next = this.querySelector('.slider-button--next');
      if (prev) prev.addEventListener('click', this.step.bind(this, -1));
      if (next) next.addEventListener('click', this.step.bind(this, 1));

      this.addEventListener('mouseenter', this.pause.bind(this));
      this.addEventListener('mouseleave', this.play.bind(this));
      this.addEventListener('focusin', this.pause.bind(this));
      this.addEventListener('focusout', this.play.bind(this));

      this.play();
    }

    disconnectedCallback() {
      this.pause();
    }

    step(direction) {
      this.index = (this.index + direction + this.slides.length) % this.slides.length;
      // No `behavior` option — the animation comes from `scroll-behavior: smooth`
      // on `.slider--everywhere`, same as Dawn. Passing behavior:'smooth'
      // explicitly gets cancelled by the scroll-snap container.
      this.slider.scrollTo({ left: this.slides[this.index].offsetLeft });
    }

    play() {
      if (!this.autoplay || this.timer) return;
      this.timer = setInterval(this.step.bind(this, 1), this.speed);
    }

    pause() {
      clearInterval(this.timer);
      this.timer = null;
    }
  };
  if (!customElements.get('rosa-slideshow')) customElements.define('rosa-slideshow', RosaSlideshow);

  /* ------------------------------------------------------------------ *
   * Mobile menu drawer (Dawn's header-drawer).
   * ------------------------------------------------------------------ */
  var RosaHDrawer = class extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('details');
      this.summary = this.querySelector('summary');
      if (!this.details || !this.summary) return;

      this.summary.setAttribute('role', 'button');
      this.summary.setAttribute('aria-expanded', 'false');
      this.summary.addEventListener('click', this.onSummaryClick.bind(this));

      this.details.addEventListener('keyup', function (event) {
        if (event.code && event.code.toUpperCase() === 'ESCAPE') this.close();
      }.bind(this));

      document.addEventListener('click', function (event) {
        if (!this.details.hasAttribute('open')) return;
        if (!this.contains(event.target)) this.close();
      }.bind(this));
    }

    onSummaryClick(event) {
      // Clicking a <summary> natively toggles the `open` attribute, so opening
      // must NOT set it here — doing so would let the default action toggle it
      // straight back off. Closing has to cancel the default action instead, so
      // the drawer stays mounted for its slide-out transition.
      if (this.details.hasAttribute('open')) {
        event.preventDefault();
        this.close();
      } else {
        this.open();
      }
    }

    open() {
      setViewportVars();
      // Deferred by a task so the translate(-100%) start state is committed
      // first — a timeout rather than rAF, which is suspended in hidden tabs.
      setTimeout(function () {
        this.details.classList.add('menu-opening');
      }.bind(this), 0);
      this.summary.setAttribute('aria-expanded', 'true');
      document.body.classList.add('rosa-drawer-open');
    }

    close() {
      var details = this.details;
      details.classList.remove('menu-opening');
      this.summary.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('rosa-drawer-open');

      var drawer = details.querySelector('.menu-drawer');
      var done = function () {
        details.removeAttribute('open');
        if (drawer) drawer.removeEventListener('transitionend', done);
      };
      if (drawer) {
        drawer.addEventListener('transitionend', done);
        setTimeout(done, 400);
      } else {
        done();
      }
    }
  };
  if (!customElements.get('rosa-hdrawer')) customElements.define('rosa-hdrawer', RosaHDrawer);

  /* ------------------------------------------------------------------ *
   * Search modal (Dawn's details-modal).
   * ------------------------------------------------------------------ */
  var RosaDModal = class extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('details');
      this.summary = this.querySelector('summary');
      if (!this.details || !this.summary) return;

      this.summary.setAttribute('role', 'button');
      this.summary.setAttribute('aria-expanded', 'false');
      this.summary.addEventListener('click', this.onSummaryClick.bind(this));

      this.details.addEventListener('keyup', function (event) {
        if (event.code && event.code.toUpperCase() === 'ESCAPE') this.close();
      }.bind(this));

      var closeButton = this.querySelector('.modal__close-button');
      if (closeButton) closeButton.addEventListener('click', this.close.bind(this));

      var overlay = this.querySelector('.modal-overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));
    }

    onSummaryClick(event) {
      // See the note in rosa-hdrawer: the native <summary> toggle does the
      // opening, this only decorates it.
      if (this.details.hasAttribute('open')) {
        event.preventDefault();
        this.close();
      } else {
        this.open();
      }
    }

    open() {
      this.summary.setAttribute('aria-expanded', 'true');
      document.body.classList.add('rosa-modal-open');
      var input = this.querySelector('input[type="search"]');
      if (input) setTimeout(function () { input.focus(); }, 50);
    }

    close() {
      this.details.removeAttribute('open');
      this.summary.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('rosa-modal-open');
    }
  };
  if (!customElements.get('rosa-dmodal')) customElements.define('rosa-dmodal', RosaDModal);

  /* ------------------------------------------------------------------ *
   * Country/region disclosure inside the drawer (Dawn's localization-form).
   * ------------------------------------------------------------------ */
  var RosaLForm = class extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('.disclosure__button');
      this.panel = this.querySelector('.disclosure__list-wrapper');
      if (!this.button || !this.panel) return;

      this.button.addEventListener('click', function () {
        var expanded = this.button.getAttribute('aria-expanded') === 'true';
        this.button.setAttribute('aria-expanded', String(!expanded));
        this.panel.hidden = expanded;
      }.bind(this));

      this.addEventListener('click', function (event) {
        var link = event.target.closest('a[data-value]');
        if (!link) return;
        event.preventDefault();
        var input = this.querySelector('input[name="country_code"], input[name="locale_code"]');
        if (input) input.value = link.dataset.value;
        var form = this.querySelector('form');
        if (form) form.submit();
      }.bind(this));

      document.addEventListener('click', function (event) {
        if (this.contains(event.target)) return;
        this.button.setAttribute('aria-expanded', 'false');
        this.panel.hidden = true;
      }.bind(this));
    }
  };
  if (!customElements.get('rosa-lform')) customElements.define('rosa-lform', RosaLForm);

  /* ------------------------------------------------------------------ *
   * Homepage interactions (target's homepage_script.js, de-jQuery'd).
   * ------------------------------------------------------------------ */
  function slideToggle(element, duration) {
    duration = duration || 400;
    var hidden = window.getComputedStyle(element).display === 'none';

    if (hidden) {
      element.style.display = 'block';
      var target = element.scrollHeight;
      element.style.overflow = 'hidden';
      element
        .animate([{ height: '0px' }, { height: target + 'px' }], { duration: duration, easing: 'ease' })
        .addEventListener('finish', function () {
          element.style.overflow = '';
        });
    } else {
      var current = element.scrollHeight;
      element.style.overflow = 'hidden';
      element
        .animate([{ height: current + 'px' }, { height: '0px' }], { duration: duration, easing: 'ease' })
        .addEventListener('finish', function () {
          element.style.display = 'none';
          element.style.overflow = '';
        });
    }
  }

  function initFaq(scope) {
    scope.querySelectorAll('.homepage-faq_block').forEach(function (block) {
      block.addEventListener('click', function () {
        var content = block.querySelector('.homepage-faq_content');
        if (content) slideToggle(content);
        block.classList.toggle('active');
      });
    });
  }

  function initSpecs(scope) {
    scope.querySelectorAll('.homepage-spec_block').forEach(function (tab) {
      tab.addEventListener('click', function () {
        var section = tab.closest('.rosa-specs') || scope;
        section.querySelectorAll('.homepage-spec_block').forEach(function (other) {
          other.classList.remove('active');
        });
        tab.classList.add('active');

        var id = tab.getAttribute('data-id');
        section.querySelectorAll('.homepage-specs_container').forEach(function (panel) {
          panel.classList.add('hidden');
        });
        section
          .querySelectorAll('.homepage-specs_container[data-id="' + id + '"]')
          .forEach(function (panel) {
            panel.classList.remove('hidden');
          });
      });
    });
  }

  function initAddToCart(scope) {
    scope.querySelectorAll('.homepage-collection_atc[data-id]').forEach(function (button) {
      button.addEventListener('click', function () {
        var id = button.getAttribute('data-id');
        if (!id || button.dataset.busy === 'true') return;

        button.dataset.busy = 'true';
        var label = button.textContent;
        button.textContent = 'ADDING…';

        fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] })
        })
          .then(function (response) {
            if (!response.ok) throw new Error('add to cart failed');
            window.location.href = '/cart';
          })
          .catch(function () {
            button.textContent = label;
            button.dataset.busy = 'false';
          });
      });
    });
  }

  function init(scope) {
    initFaq(scope);
    initSpecs(scope);
    initAddToCart(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  // Theme editor re-renders a section: re-bind inside it only.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
    setViewportVars();
  });
})();
